import React, { useState, useRef } from 'react';
import styles from './FeedbackCollector.module.css';

const FeedbackCollector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feature');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [collectDiagnostics, setCollectDiagnostics] = useState(true);
  const modalRef = useRef(null);

  const getDiagnosticInfo = () => {
    if (!collectDiagnostics) return null;

    return {
      browser: navigator.userAgent,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      language: navigator.language
    };
  };

  const handleScreenshot = async () => {
    try {
      // Temporarily hide the modal
      if (modalRef.current) {
        modalRef.current.style.display = 'none';
      }

      // Wait a brief moment for the modal to hide
      await new Promise(resolve => setTimeout(resolve, 100));

      const stream = await navigator.mediaDevices.getDisplayMedia({ preferCurrentTab: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      setScreenshot(blob);

      stream.getTracks().forEach(track => track.stop());

      // Show the modal again
      if (modalRef.current) {
        modalRef.current.style.display = 'flex';
      }
    } catch (error) {
      console.error('Screenshot failed:', error);
      // Ensure modal is visible again if there's an error
      if (modalRef.current) {
        modalRef.current.style.display = 'flex';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('type', feedbackType);
    formData.append('description', description);

    if (screenshot) {
      formData.append('screenshot', screenshot);
    }

    if (collectDiagnostics) {
      formData.append('diagnostics', JSON.stringify(getDiagnosticInfo()));
    }

    try {
      // Use the API base URL from environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? 'https://api.lotwleague.com'
        : 'http://localhost:3001';

      const response = await fetch(`${apiUrl}/api/feedback`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });

      console.log('Response status:', response.status); // Debug log

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText); // Debug log
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`Feedback submitted successfully! Issue: ${result.issueKey}`);
        setIsOpen(false);
        setDescription('');
        setScreenshot(null);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <>
      <button
        className={styles.feedbackButton}
        onClick={() => setIsOpen(true)}
      >
        Feedback
      </button>

      {isOpen && (
        <div className={styles.modal} ref={modalRef}>
          <div className={styles.modalContent}>
            <h2>Submit Feedback</h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="feedbackType">Type</label>
                <select
                  id="feedbackType"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                >
                  <option value="feature">Feature Request</option>
                  <option value="bug">Report Bug</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="screenshot">Screenshot</label>
                <button
                  id="screenshot"
                  type="button"
                  onClick={handleScreenshot}
                  className={styles.screenshotButton}
                >
                  Capture Screenshot
                </button>
                {screenshot && <span>Screenshot captured!</span>}
              </div>

              <div className={styles.formGroup}>
                <label>
                  <input
                    type="checkbox"
                    checked={collectDiagnostics}
                    onChange={(e) => setCollectDiagnostics(e.checked)}
                  />
                  {' '}Include system diagnostics
                </label>
              </div>

              <div className={styles.buttonGroup}>
                <button type="submit">Submit</button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackCollector;
